import {getTruthPassMessage} from "@/i18n/truthPassMessages.js";
import { getClinicalMessage } from "@/i18n/clinicalMessages.js";
import React from "react";
import supabase from "@/services/supabaseClient";
import {
  getActivatedAppLanguages,
  applyDocumentLanguage,
  LANGUAGE_CHANGE_EVENT,
  LANGUAGE_STORAGE_KEY,
  getInitialLanguageCode,
  getLanguageLabel,
  normalizeAppLanguageCode,
  setLanguagePreference,
} from "@/config/languages.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import {
  COUNTRY_CHANGE_EVENT,
  COUNTRY_STORAGE_KEY,
  getInitialMarketIso,
  getMarketByIso,
  setMarketPreference,
} from "@/config/markets.js";
import { LANGUAGE_MESSAGES } from "@/i18n/languageMessages.js";
import { LANGUAGE_MESSAGE_PATCHES } from "@/i18n/languageMessagePatches.js";
import { getPaymentMessage } from "@/i18n/paymentMessages.js";
import {
  failLocalePersistence,
  optionalLanguageCode,
  reconcileProfileLocale,
  selectLocaleState,
} from "./languageLifecycle.mjs";

export const LanguageContext = React.createContext(null);

const PRE_AUTH_LANGUAGE_INTENT_KEY = "afrimed:preauth-language-intent";
const PROFILE_LANGUAGE_PENDING_KEY = "afrimed:profile-language-pending";

function readPreAuthLanguageIntent() {
  try {
    return optionalLanguageCode(
      window.sessionStorage?.getItem(PRE_AUTH_LANGUAGE_INTENT_KEY) || "",
      normalizeAppLanguageCode,
    );
  } catch {
    return "";
  }
}

function writePreAuthLanguageIntent(languageCode = "") {
  try {
    const normalized = normalizeAppLanguageCode(languageCode);
    if (normalized) window.sessionStorage?.setItem(PRE_AUTH_LANGUAGE_INTENT_KEY, normalized);
  } catch {
    // The in-memory context still preserves the user's choice for this session.
  }
}

function clearPreAuthLanguageIntent() {
  try {
    window.sessionStorage?.removeItem(PRE_AUTH_LANGUAGE_INTENT_KEY);
  } catch {
    // ignore storage failures
  }
}

function readPendingProfileLanguage(userId) {
  if (!userId) return "";
  try {
    const pending = JSON.parse(window.sessionStorage?.getItem(PROFILE_LANGUAGE_PENDING_KEY) || "null");
    return pending?.userId === userId
      ? optionalLanguageCode(pending.languageCode, normalizeAppLanguageCode)
      : "";
  } catch {
    return "";
  }
}

function writePendingProfileLanguage(userId, languageCode = "") {
  if (!userId) return;
  try {
    const normalized = optionalLanguageCode(languageCode, normalizeAppLanguageCode);
    if (normalized) window.sessionStorage?.setItem(
      PROFILE_LANGUAGE_PENDING_KEY,
      JSON.stringify({ userId, languageCode: normalized }),
    );
  } catch {
    // The in-memory ref still protects the current selection.
  }
}

function clearPendingProfileLanguage(userId) {
  if (!userId) return;
  try {
    const pending = JSON.parse(window.sessionStorage?.getItem(PROFILE_LANGUAGE_PENDING_KEY) || "null");
    if (pending?.userId === userId) window.sessionStorage?.removeItem(PROFILE_LANGUAGE_PENDING_KEY);
  } catch {
    // ignore storage failures
  }
}

function deepMergeMessages(base, patch) {
  if (!patch || typeof patch !== "object") return base;
  const output = { ...(base && typeof base === "object" ? base : {}) };

  for (const [key, value] of Object.entries(patch)) {
    const current = output[key];
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      current &&
      typeof current === "object" &&
      !Array.isArray(current)
    ) {
      output[key] = deepMergeMessages(current, value);
      continue;
    }
    output[key] = value;
  }

  return output;
}

function resolveMessages(locale) {
  const normalized = normalizeAppLanguageCode(locale);
  const short = normalized.split("-")[0];
  const englishBase = LANGUAGE_MESSAGES["en-GB"] || {};
  const englishPatch =
    LANGUAGE_MESSAGE_PATCHES["en-GB"] ||
    LANGUAGE_MESSAGE_PATCHES.en ||
    null;
  const localizedBase =
    normalized === "en-GB"
      ? englishBase
      : LANGUAGE_MESSAGES[normalized] || LANGUAGE_MESSAGES[short] || {};
  const fallbackBase =
    normalized === "en-GB"
      ? englishBase
      : deepMergeMessages(englishBase, localizedBase);
  const fallback = englishPatch
    ? deepMergeMessages(fallbackBase, englishPatch)
    : fallbackBase;
  const patch =
    LANGUAGE_MESSAGE_PATCHES[normalized] ||
    LANGUAGE_MESSAGE_PATCHES[short] ||
    null;
  if (!patch) return fallback;
  return deepMergeMessages(fallback, patch);
}

function isPluralDescriptor(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value.__type === "plural"
  );
}

function isSelectDescriptor(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    value.__type === "select" &&
    value.cases &&
    typeof value.cases === "object"
  );
}

function getNestedValue(source, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, segment) => {
      if (!current || typeof current !== "object") return undefined;
      return current[segment];
    }, source);
}

function getFallbackAliasKey(path) {
  const key = String(path || "").trim();
  if (key.startsWith("prescriptions.")) {
    return `doctorDashboard.${key.slice("prescriptions.".length)}`;
  }
  return "";
}

function interpolate(template, values) {
  if (typeof template !== "string") return template;
  if (!values || typeof values !== "object") return template;

  return template.replace(/\{(\w+)\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(values, key)
      ? String(values[key])
      : `{${key}}`
  );
}

function coerceDate(value) {
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const CUSTOM_DATE_LOCALES = {
  sn: {
    months: {
      long: [
        "Ndira",
        "Kukadzi",
        "Kurume",
        "Kubvumbi",
        "Chivabvu",
        "Chikumi",
        "Chikunguru",
        "Nyamavhuvhu",
        "Gunyana",
        "Gumiguru",
        "Mbudzi",
        "Zvita",
      ],
      short: [
        "Ndi",
        "Kuk",
        "Kur",
        "Kub",
        "Chi",
        "Chk",
        "Chg",
        "Nya",
        "Gun",
        "Gum",
        "Mbu",
        "Zvi",
      ],
    },
    weekdays: {
      long: [
        "Svondo",
        "Muvhuro",
        "Chipiri",
        "Chitatu",
        "China",
        "Chishanu",
        "Mugovera",
      ],
      short: ["Sv", "Muv", "Chi", "Chit", "China", "Chish", "Mug"],
    },
  },
  zu: {
    months: {
      long: [
        "Januwari",
        "Febhuwari",
        "Mashi",
        "Ephreli",
        "Meyi",
        "Juni",
        "Julayi",
        "Agasti",
        "Septhemba",
        "Okthoba",
        "Novemba",
        "Disemba",
      ],
      short: [
        "Jan",
        "Feb",
        "Mas",
        "Eph",
        "Mey",
        "Jun",
        "Jul",
        "Aga",
        "Sep",
        "Okt",
        "Nov",
        "Dis",
      ],
    },
    weekdays: {
      long: [
        "ISonto",
        "UMsombuluko",
        "ULwesibili",
        "ULwesithathu",
        "ULwesine",
        "ULwesihlanu",
        "UMgqibelo",
      ],
      short: ["Son", "Mso", "Bil", "Tha", "Sin", "Hla", "Mgq"],
    },
  },
};

function padDateNumber(value, style = "numeric") {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return style === "2-digit" ? String(numeric).padStart(2, "0") : String(numeric);
}

function formatCustomLocaleDate(languageCode, value, options = {}) {
  const date = coerceDate(value);
  const localeData = CUSTOM_DATE_LOCALES[String(languageCode || "").trim().toLowerCase()];
  if (!date || !localeData) return null;

  const weekdayStyle = options.weekday === "long" ? "long" : options.weekday ? "short" : "";
  const monthStyle = options.month === "long" ? "long" : options.month ? "short" : "";

  const weekdayLabel = weekdayStyle
    ? localeData.weekdays[weekdayStyle][date.getDay()] || ""
    : "";
  const monthLabel = monthStyle
    ? localeData.months[monthStyle][date.getMonth()] || ""
    : "";

  const dateParts = [];
  if (options.day) dateParts.push(padDateNumber(date.getDate(), options.day));
  if (monthLabel) dateParts.push(monthLabel);
  if (options.year) {
    const yearValue =
      options.year === "2-digit"
        ? String(date.getFullYear()).slice(-2)
        : String(date.getFullYear());
    dateParts.push(yearValue);
  }

  let result = dateParts.join(" ");
  if (weekdayLabel) {
    result = result ? `${weekdayLabel}, ${result}` : weekdayLabel;
  }

  if (options.hour || options.minute) {
    const timeLabel = [
      options.hour ? padDateNumber(date.getHours(), options.hour) : "",
      options.minute ? padDateNumber(date.getMinutes(), options.minute) : "",
    ]
      .filter(Boolean)
      .join(":");
    result = result ? `${result}, ${timeLabel}` : timeLabel;
  }

  return result;
}

export function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}

export function LanguageProvider({ children }) {
  const auth = useAuth() || {};
  const userId = String(auth?.user?.id || "").trim();
  const profileLanguageCode = optionalLanguageCode(
    auth?.profile?.preferred_app_language || "",
    normalizeAppLanguageCode,
  );
  const pendingProfileLanguageRef = React.useRef(readPendingProfileLanguage(userId));
  const pendingProfileUserRef = React.useRef(userId);
  const persistenceQueueRef = React.useRef(Promise.resolve());
  const persistenceRevisionRef = React.useRef(0);
  if (pendingProfileUserRef.current !== userId) {
    pendingProfileUserRef.current = userId;
    pendingProfileLanguageRef.current = readPendingProfileLanguage(userId);
    persistenceRevisionRef.current += 1;
  }
  // A visitor can deliberately choose a language before signing in. Keep that
  // explicit choice for the current sign-in journey instead of immediately
  // repainting the first authenticated route in the saved profile language.
  // This is intent metadata within the existing locale context, not a second
  // locale store; authenticated changes still persist through setLanguage.
  const preAuthLanguageOverrideRef = React.useRef(readPreAuthLanguageIntent());
  const synchronisedPreAuthLanguageRef = React.useRef("");
  const warnedMissingKeysRef = React.useRef(new Set());
  const [languageCode, setLanguageCode] = React.useState(() =>
    getInitialLanguageCode()
  );
  const [localePersistence, setLocalePersistence] = React.useState({
    status: "idle",
    error: "",
  });
  const [marketIso, setMarketIso] = React.useState(() => getInitialMarketIso());

  React.useEffect(() => {
    applyDocumentLanguage(languageCode);
  }, [languageCode]);

  React.useEffect(() => {
    if (!userId) return;
    const preAuthLanguageOverride = optionalLanguageCode(
      preAuthLanguageOverrideRef.current || readPreAuthLanguageIntent(),
      normalizeAppLanguageCode,
    );
    if (preAuthLanguageOverride) {
      preAuthLanguageOverrideRef.current = preAuthLanguageOverride;
      if (preAuthLanguageOverride !== languageCode) {
        setLanguagePreference(preAuthLanguageOverride);
        setLanguageCode(preAuthLanguageOverride);
      }
      // Keep the explicit choice through authenticated hydration. Once the
      // profile reflects it, this one-session intent can retire safely.
      if (profileLanguageCode === preAuthLanguageOverride) {
        preAuthLanguageOverrideRef.current = "";
        clearPreAuthLanguageIntent();
        return;
      }

      const syncKey = `${userId}:${preAuthLanguageOverride}`;
      if (synchronisedPreAuthLanguageRef.current !== syncKey && !auth?.BYPASS) {
        synchronisedPreAuthLanguageRef.current = syncKey;
        void supabase
          .from("profiles")
          .update({ preferred_app_language: preAuthLanguageOverride })
          .eq("user_id", userId)
          .then(({ error }) => {
            if (!error) void auth?.refreshProfile?.();
          })
          .catch(() => {});
      }
      return;
    }
    const preferred = String(auth?.profile?.preferred_app_language || "").trim();
    if (!preferred) return;

    const reconciliation = reconcileProfileLocale({
      languageCode,
      profileLanguageCode,
      pendingProfileLanguage: pendingProfileLanguageRef.current || readPendingProfileLanguage(userId),
      persistenceStatus: localePersistence.status,
    });
    pendingProfileLanguageRef.current = reconciliation.pendingProfileLanguage;
    if (reconciliation.profileConfirmed) {
      clearPendingProfileLanguage(userId);
      setLocalePersistence({ status: "idle", error: "" });
      return;
    }
    if (reconciliation.preserveSelection) {
      if (reconciliation.languageCode !== languageCode) {
        setLanguagePreference(reconciliation.languageCode);
        setLanguageCode(reconciliation.languageCode);
      }
      return;
    }
    if (reconciliation.languageCode !== languageCode) {
      setLanguagePreference(reconciliation.languageCode);
      setLanguageCode(reconciliation.languageCode);
    }
  }, [
    auth?.profile?.preferred_app_language,
    languageCode,
    localePersistence.status,
    profileLanguageCode,
    userId,
  ]);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncLanguage = (nextCode) => {
      setLanguageCode(normalizeAppLanguageCode(nextCode || getInitialLanguageCode()));
    };

    const handleLanguageChange = (event) => {
      syncLanguage(event?.detail?.code);
    };

    const handleCountryChange = (event) => {
      setMarketIso(
        getMarketByIso(event?.detail?.iso)?.iso || getInitialMarketIso()
      );
    };

    const handleStorage = (event) => {
      if (!event?.key) return;
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event?.newValue);
        return;
      }
      if (event.key === COUNTRY_STORAGE_KEY) {
        setMarketIso(getInitialMarketIso());
      }
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener(COUNTRY_CHANGE_EVENT, handleCountryChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      window.removeEventListener(COUNTRY_CHANGE_EVENT, handleCountryChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const setLanguage = React.useCallback((nextCode) => {
    const normalized = setLanguagePreference(nextCode);
    const selection = selectLocaleState({
      languageCode,
      nextLanguageCode: normalized,
      authenticated: Boolean(userId),
      bypass: Boolean(auth?.BYPASS),
    });
    setLanguageCode(selection.languageCode);
    setLocalePersistence({ status: selection.persistenceStatus, error: "" });

    if (!userId) {
      preAuthLanguageOverrideRef.current = normalized;
      writePreAuthLanguageIntent(normalized);
      return normalized;
    }

    preAuthLanguageOverrideRef.current = "";
    clearPreAuthLanguageIntent();
    pendingProfileLanguageRef.current = normalized;
    writePendingProfileLanguage(userId, normalized);

    if (auth?.BYPASS) return normalized;

    const revision = persistenceRevisionRef.current + 1;
    persistenceRevisionRef.current = revision;
    persistenceQueueRef.current = persistenceQueueRef.current
      .catch(() => {})
      .then(async () => {
        const { error } = await supabase
          .from("profiles")
          .upsert(
            {
              user_id: userId,
              preferred_app_language: normalized,
            },
            { onConflict: "user_id" }
          );
        if (revision !== persistenceRevisionRef.current) return;
        if (error) {
          const failure = failLocalePersistence({
            languageCode: normalized,
            pendingProfileLanguage: normalized,
            error: error.message,
          });
          setLocalePersistence({ status: failure.persistenceStatus, error: failure.persistenceError });
          console.warn("[i18n] Language preference persistence failed; retaining the local selection.", {
            code: error.code || "",
          });
          return;
        }
        setLocalePersistence({ status: "awaiting_profile", error: "" });
        await auth?.refreshProfile?.();
      })
      .catch((error) => {
        if (revision !== persistenceRevisionRef.current) return;
        const failure = failLocalePersistence({
          languageCode: normalized,
          pendingProfileLanguage: normalized,
          error: error?.message,
        });
        setLocalePersistence({ status: failure.persistenceStatus, error: failure.persistenceError });
        console.warn("[i18n] Language preference persistence failed; retaining the local selection.");
      });

    return normalized;
  }, [auth, languageCode, userId]);

  const setMarket = React.useCallback((nextIso) => {
    const normalized = setMarketPreference(nextIso);
    setMarketIso(normalized);
    return normalized;
  }, []);

  const messages = React.useMemo(() => resolveMessages(languageCode), [languageCode]);
  const englishMessages = React.useMemo(() => resolveMessages("en-GB"), []);
  const market = React.useMemo(() => getMarketByIso(marketIso), [marketIso]);
  const appLanguages = React.useMemo(() => getActivatedAppLanguages(), []);

  const resolveDescriptor = React.useCallback(
    (value, fallback = "", values = undefined) => {
      if (typeof value === "string") {
        return interpolate(value, values);
      }

      if (isPluralDescriptor(value)) {
        const count = Number(values?.count ?? values?.value ?? 0);
        const pluralRules = new Intl.PluralRules(languageCode);
        const category = pluralRules.select(Number.isFinite(count) ? count : 0);
        const template =
          value[String(count)] ||
          value[category] ||
          value.other ||
          fallback ||
          "";
        return interpolate(template, values);
      }

      if (isSelectDescriptor(value)) {
        const selector = String(
          values?.select ??
            values?.value ??
            values?.gender ??
            values?.role ??
            "other"
        );
        const template =
          value.cases?.[selector] ||
          value.cases?.other ||
          fallback ||
          "";
        return interpolate(template, values);
      }

      return undefined;
    },
    [languageCode]
  );

  const t = React.useCallback(
    (key, fallback = "", values = undefined) => {
      const truth = getTruthPassMessage(languageCode, key);
      if (truth != null) return truth;
      const clinical = getClinicalMessage(languageCode, key);
      if (clinical) return resolveDescriptor(clinical, "", values);
      // Payout copy belongs to the governed bundle, ahead of inherited English defaults.
      if (String(key).startsWith("settings.payoutPreferences.") || String(key).startsWith("settings.preferences.plans.")) {
        const payoutMessage = resolveDescriptor(getPaymentMessage(languageCode, key), "", values);
        if (typeof payoutMessage === "string") return payoutMessage;
      }
      const localized = getNestedValue(messages, key);
      const english = getNestedValue(englishMessages, key);
      const aliasKey = getFallbackAliasKey(key);

      const localizedResolved = resolveDescriptor(localized, "", values);
      if (typeof localizedResolved === "string") return localizedResolved;

      const paymentResolved = resolveDescriptor(
        getPaymentMessage(languageCode, key),
        "",
        values,
      );
      if (typeof paymentResolved === "string") return paymentResolved;

      if (aliasKey) {
        const localizedAliasResolved = resolveDescriptor(
          getNestedValue(messages, aliasKey),
          "",
          values
        );
        if (typeof localizedAliasResolved === "string") return localizedAliasResolved;
      }

      const englishResolved = resolveDescriptor(english, "", values);
      if (typeof englishResolved === "string") {
        if (import.meta.env.DEV && !warnedMissingKeysRef.current.has(`${languageCode}:${key}`)) {
          warnedMissingKeysRef.current.add(`${languageCode}:${key}`);
          console.warn(
            `[i18n] Missing localized key "${key}" for ${languageCode}. Falling back to English.`,
          );
        }
        return englishResolved;
      }

      if (aliasKey) {
        const englishAliasResolved = resolveDescriptor(
          getNestedValue(englishMessages, aliasKey),
          "",
          values
        );
        if (typeof englishAliasResolved === "string") {
          if (import.meta.env.DEV && !warnedMissingKeysRef.current.has(`${languageCode}:${key}`)) {
            warnedMissingKeysRef.current.add(`${languageCode}:${key}`);
            console.warn(
              `[i18n] Missing localized key "${key}" for ${languageCode}. Falling back to alias "${aliasKey}".`,
            );
          }
          return englishAliasResolved;
        }
      }

      if (import.meta.env.DEV && !warnedMissingKeysRef.current.has(`missing:${key}`)) {
        warnedMissingKeysRef.current.add(`missing:${key}`);
        console.warn(`[i18n] Missing translation key "${key}" in all active language bundles.`);
      }

      return interpolate(fallback || key, values);
    },
    [englishMessages, languageCode, messages, resolveDescriptor]
  );

  const tPlural = React.useCallback(
    (key, count, fallback = "", values = undefined) =>
      t(key, fallback, { ...(values || {}), count }),
    [t]
  );

  const tSelect = React.useCallback(
    (key, select, fallback = "", values = undefined) =>
      t(key, fallback, { ...(values || {}), select }),
    [t]
  );

  const formatDate = React.useCallback(
    (value, options = {}) => {
      const date = coerceDate(value);
      if (!date) return "";
      const custom = formatCustomLocaleDate(languageCode, date, options);
      if (custom) return custom;
      return new Intl.DateTimeFormat(languageCode, options).format(date);
    },
    [languageCode]
  );

  const formatTime = React.useCallback(
    (value, options = {}) => {
      const date = coerceDate(value);
      if (!date) return "";
      const custom = formatCustomLocaleDate(languageCode, date, {
        hour: "2-digit",
        minute: "2-digit",
        ...options,
      });
      if (custom) return custom;
      return new Intl.DateTimeFormat(languageCode, {
        hour: "numeric",
        minute: "2-digit",
        ...options,
      }).format(date);
    },
    [languageCode]
  );

  const formatDateTime = React.useCallback(
    (value, options = {}) => {
      const date = coerceDate(value);
      if (!date) return "";
      const custom = formatCustomLocaleDate(languageCode, date, options);
      if (custom) return custom;
      return new Intl.DateTimeFormat(languageCode, options).format(date);
    },
    [languageCode]
  );

  const value = React.useMemo(
    () => ({
      languageCode,
      languageLabel: getLanguageLabel(languageCode),
      languages: appLanguages,
      appLanguages,
      marketIso,
      market,
      localePersistence,
      setLanguage,
      setMarket,
      t,
      tPlural,
      tSelect,
      formatDate,
      formatTime,
      formatDateTime,
    }),
    [
      appLanguages,
      formatDate,
      formatDateTime,
      formatTime,
      languageCode,
      localePersistence,
      market,
      marketIso,
      setLanguage,
      setMarket,
      t,
      tPlural,
      tSelect,
    ]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageProvider;
